// ─────────────────────────────────────────────────────────────────────────────
// D3 — la columna «Acciones» de las DOS tablas de campañas de Reportería (C2/P18).
//
// 🔴 Este archivo antes ofrecía una acción distinta por estado (enviar a aprobación, pausar,
// relanzar), mapeada a los comandos de dominio del editor de campañas. Ese diseño queda REVERTIDO:
// Reportería es una pantalla de LECTURA y esas acciones ya viven —completas— en `admin.campaigns`
// (`AdminCampaigns.tsx`/`MisSolicitudesCampana.tsx`, que usan `useCampaignApproval`/
// `usePanicCampaign`/`useDuplicateCampaign`). No hay pérdida funcional: solo deja de duplicarse aquí.
//
// Queda un único botón «Descargar Excel» por fila, igual para cualquier estado de la campaña.
//
// D6 — conecta el placeholder de D3 al endpoint de export de AnalyticsService-api (Tarea D5,
// `GET v1/analytics/recognition/campaign-export`) y arma el Excel con `exportMultiSheetExcel`
// (Tarea A3). El backend YA audita cada 200 exitoso dentro del propio handler
// (`IRecognitionExportAuditRepository`): a diferencia de `DemograficoExportButton.tsx` (Fase C),
// aquí NO hay una llamada adicional de auditoría — repetirla duplicaría el registro.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, useToast } from '@/components/common';
import { recognitionReportsApi, getCampaignName, getRecognitionErrorMessage } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import {
  exportMultiSheetExcel, buildResumenEjecutivoSheet, buildDefinicionesSheet,
} from '@/utils/exportExcelMultiSheet';
import { campaignDisplayStatus } from '../reportsShared';

const SIN_DATO = '—';
// Nombre de pestaña LITERAL (no `t()`), igual criterio que `DemograficoExportButton.tsx`: el
// contenido del Excel no cambia con el idioma de quien exporta.
const NOMBRE_HOJA_DETALLE = 'Detalle';

/**
 * Fecha formateada con el locale del navegador, o `null` si no hay valor — nunca una cadena ISO
 * cruda en el Excel.
 */
function formatFecha(fecha: string | null): string | null {
  return fecha ? new Date(fecha).toLocaleDateString() : null;
}

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
export function CampaignRowActions(
  { campaign, canExportData = true }: Readonly<{ campaign: CampaignDto; canExportData?: boolean }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { toast, showError } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  async function handleDownload() {
    setIsDownloading(true);
    try {
      const payload = await recognitionReportsApi.getCampaignExport(campaign.id);

      const periodo = [formatFecha(campaign.fechaInicio), formatFecha(campaign.fechaFin)]
        .filter((parte): parte is string => Boolean(parte))
        .join(' – ') || SIN_DATO;
      const estado = t(`reports.campaigns.status.${campaignDisplayStatus(campaign.estado)}`);

      // RN de anonimato (mismo criterio que `CampaignsGeneralTab`/`summaryCell`): con `suprimido`,
      // TODAS las celdas numéricas salen «—», nunca un 0 — aunque el backend sí devuelva la
      // audiencia real (no es actividad individual, ver doc-comment de `CampaignSummaryDto`), la
      // vista la enmascara igual que el resto del panel para no revelar el conteo a medias.
      // Labels vía `t()`: son las mismas claves que ya usan los headers de columna de
      // `CampaignsGeneralTab.tsx` (`colAudience`/`colStatus`/`colRecognitions`/`colRate`/
      // `colAvgUser`/`colParticipants`) — un mismo dato no puede tener dos nombres según la pantalla
      // desde la que se exporte. Solo la prosa libre (`queEs`/`comoSeCalcula`) sigue el precedente de
      // `DemograficoExportButton.tsx` y queda literal en español: el Excel no cambia con el idioma.
      const resumen = buildResumenEjecutivoSheet(
        t('reports.title'),
        getCampaignName(campaign, lang),
        periodo,
        SIN_DATO,
        [
          { label: t('reports.campaigns.colAudience'), valor: payload.suprimido ? SIN_DATO : payload.audiencia },
          { label: t('reports.campaigns.colStatus'), valor: estado },
          { label: t('reports.campaigns.colRecognitions'), valor: payload.suprimido ? SIN_DATO : payload.reconocimientos },
        ],
      );

      // Mismas definiciones documentadas al inicio de `CampaignsGeneralTab.tsx` (hoja «Definiciones»
      // del Excel del cliente) — no se inventa texto nuevo, se replica.
      const definiciones = buildDefinicionesSheet([
        {
          dato: t('reports.campaigns.colRate'),
          queEs: 'Porcentaje de audiencia objetivo que participó (reconociendo o siendo reconocida) en la campaña.',
          comoSeCalcula: 'Participantes / audiencia objetivo de la campaña.',
        },
        {
          dato: t('reports.campaigns.colAvgUser'),
          queEs: 'Reconocimientos promedio por cada participante de la campaña.',
          comoSeCalcula: 'Reconocimientos / participantes de esa campaña.',
        },
        {
          dato: t('reports.campaigns.colParticipants'),
          queEs: 'Personas únicas que participaron, reconociendo o siendo reconocidas.',
          comoSeCalcula: 'Conteo de personas únicas de los recibos de proyección de la campaña.',
        },
      ]);

      // Hoja de detalle: PLACEHOLDER documentado — el propio Excel de referencia ("09 - Ejemplo de
      // otra campaña.xlsx") ya la marca como pendiente. No es un requisito cerrado de esta tarea.
      const detalle = {
        name: NOMBRE_HOJA_DETALLE,
        rows: [['Detalle de reconocimientos'], ['Pendiente de integrar.']],
      };

      // Nombre de la campaña, no su id crudo (bug QA 2026-09-10) — el mismo que ya se resuelve arriba
      // para el título dentro del libro (`resumen`). Mismo sanitizado que `LeadersTab.tsx`.
      const nombreCampanaArchivo = getCampaignName(campaign, lang);
      await exportMultiSheetExcel({
        fileName: nombreCampanaArchivo
          ? `reporteria-campana-${nombreCampanaArchivo.replace(/[^\p{L}\p{N}]+/gu, '-')}`
          : `reporteria-campana-${campaign.id}`,
        sheets: [resumen, definiciones, detalle],
      });
    } catch (error: unknown) {
      showError(getRecognitionErrorMessage(error, t('reports.campaigns.downloadError')));
      if (import.meta.env.DEV) console.error('No se pudo descargar el Excel de la campaña:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      {canExportData && (
        <button
          type="button"
          className="rr-btn rr-btn--ghost"
          data-campaign-id={campaign.id}
          onClick={handleDownload}
          disabled={isDownloading}
        >
          <i className="fas fa-file-excel" aria-hidden="true" /> {t('reports.campaigns.downloadExcel')}
        </button>
      )}
      <Toast toast={toast} />
    </>
  );
}
